import React, { useState, useEffect, useCallback } from 'react';
import { FiFileText, FiPlus, FiX, FiDownload, FiClock, FiAlertTriangle } from 'react-icons/fi';
import styles from '../Lead.module.css';
import useEsc from '../../../../hooks/useEsc';
import TextoMinuta, { marcadoresVazios } from './TextoMinuta';
import {
  listarMinutasLead, gerarMinutaLead, buscarMinuta, salvarMinuta, excluirMinuta, baixarMinuta,
  listarModelosContrato, STATUS_MINUTA, TIPOS_MODELO,
} from '../../../../services/leadContratos';

const nomeStatus = (s) => STATUS_MINUTA.find(x => x.id === s)?.label || s;
const dataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const nomeBase = (m, versao) => `${m.titulo}_v${versao}`.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\w-]+/g, '_');

/* Editor da minuta: texto à esquerda, pré-visualização à direita. Salvar
   com texto novo = versão nova; as anteriores ficam no histórico. */
export function EditorMinuta({ minutaId, onFechar, onMudou }) {
  const [m, setM] = useState(null);
  const [texto, setTexto] = useState('');
  const [status, setStatus] = useState('rascunho');
  const [vendoVersao, setVendoVersao] = useState(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  useEsc(onFechar);

  const carregar = useCallback(() => buscarMinuta(minutaId).then(r => {
    setM(r); setTexto(r.conteudo); setStatus(r.status); setVendoVersao(null);
  }).catch(e => setErro(e.message)), [minutaId]);
  useEffect(() => { carregar(); }, [carregar]);

  if (!m) {
    return (
      <div className={styles.modalOverlay}><div className={styles.modal}>
        <div className={styles.modalBody}>{erro ? <p className={styles.avisoReceitaErro}>{erro}</p> : <p>Carregando minuta...</p>}</div>
      </div></div>
    );
  }
  const alterado = texto !== m.conteudo || status !== m.status;
  const versaoVista = vendoVersao && m.versoes.find(v => v.versao === vendoVersao);
  const vazios = marcadoresVazios(texto);

  async function salvar() {
    setSalvando(true); setErro('');
    try { await salvarMinuta(m.id, { conteudo: texto, status }); await carregar(); onMudou(); } catch (e) { setErro(e.message); }
    setSalvando(false);
  }
  async function baixar(formato) {
    setErro('');
    if (alterado && !vendoVersao) { setErro('Salve as alterações antes de exportar (o arquivo sai da versão salva).'); return; }
    const versao = vendoVersao || m.versao_atual;
    try { await baixarMinuta(m.id, formato, nomeBase(m, versao), vendoVersao || undefined); } catch (e) { setErro(e.message); }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitulo}>{m.titulo}</h2>
            <p className={styles.modalSubtitulo}>
              Versão {m.versao_atual} · {nomeStatus(m.status)} · atualizada {dataHora(m.atualizado_em)}{m.atualizado_por ? ` por ${m.atualizado_por}` : ''}
            </p>
          </div>
          <button type="button" className={styles.modalBtnFechar} onClick={onFechar}><FiX size={20} /></button>
        </div>
        <div className={styles.minutaBarra}>
          <label className={styles.minutaCampo}>Status
            <select className={styles.select} value={status} onChange={e => setStatus(e.target.value)} disabled={!!vendoVersao}>
              {STATUS_MINUTA.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className={styles.minutaCampo}><FiClock size={13} /> Versões
            <select className={styles.select} value={vendoVersao || ''} onChange={e => setVendoVersao(Number(e.target.value) || null)}>
              <option value="">Atual (editar)</option>
              {m.versoes.map(v => <option key={v.versao} value={v.versao}>v{v.versao} · {nomeStatus(v.status)} · {dataHora(v.criado_em)}</option>)}
            </select>
          </label>
          {vendoVersao && (
            <button type="button" className={styles.btnConfig} onClick={() => { setTexto(versaoVista.conteudo); setVendoVersao(null); }}>
              Usar o texto da v{vendoVersao}
            </button>
          )}
          <div className={styles.minutaAcoes}>
            <button type="button" className={styles.btnConfig} onClick={() => baixar('pdf')}><FiDownload size={14} /> PDF</button>
            <button type="button" className={styles.btnConfig} onClick={() => baixar('docx')}><FiDownload size={14} /> DOCX</button>
            {!vendoVersao && (
              <button type="button" className={styles.btnSalvar} onClick={salvar} disabled={!alterado || salvando}>
                {salvando ? 'Salvando...' : texto !== m.conteudo ? `Salvar (vira v${m.versao_atual + 1})` : 'Salvar'}
              </button>
            )}
          </div>
        </div>
        {m.modelo_status === 'rascunho_revisar' && (
          <p className={styles.avisoModelo}><FiAlertTriangle size={13} /> O modelo usado ainda está como "rascunho – revisar": confira o texto com o responsável técnico antes de enviar.</p>
        )}
        {vazios.length > 0 && !vendoVersao && (
          <p className={styles.avisoReceitaErro} style={{ margin: '0 24px' }}>
            Campos sem dado no lead: {vazios.join(', ')}. Preencha no lead e gere de novo, ou complete aqui no texto.
          </p>
        )}
        {erro && <p className={styles.avisoReceitaErro} style={{ margin: '8px 24px 0' }}>{erro}</p>}
        <div className={styles.minutaCorpo}>
          {vendoVersao ? (
            <div className={styles.minutaColuna}><TextoMinuta texto={versaoVista?.conteudo} /></div>
          ) : (
            <>
              <textarea className={styles.minutaTexto} value={texto} onChange={e => setTexto(e.target.value)} spellCheck />
              <div className={styles.minutaColuna}><TextoMinuta texto={texto} /></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* Minutas do lead (etapa Documentos e Contrato): gerar a partir de um
   modelo, abrir/editar, ver status e versão. */
function MinutasLead({ leadId, antesDeGerar }) {
  const [minutas, setMinutas] = useState([]);
  const [sugestao, setSugestao] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [modeloSel, setModeloSel] = useState('');
  const [aberta, setAberta] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(() => listarMinutasLead(leadId)
    .then(r => { setMinutas(r.items || []); setSugestao(r.sugestao || []); })
    .catch(e => setErro(e.message)), [leadId]);
  useEffect(() => { carregar(); listarModelosContrato().then(r => setModelos(r.items || [])).catch(e => setErro(e.message)); }, [carregar]);

  /* sugeridos pelo fluxo primeiro (ex.: Abertura -> Abertura + Mensal) */
  const ordenados = [...modelos].sort((a, b) => {
    const ia = sugestao.indexOf(a.tipo); const ib = sugestao.indexOf(b.tipo);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const selecionado = modeloSel || ordenados.find(md => !minutas.some(mi => mi.modelo_id === md.id) && sugestao.includes(md.tipo))?.id || ordenados[0]?.id || '';

  async function gerar() {
    if (!selecionado) return;
    setGerando(true); setErro('');
    try {
      /* grava o que está na tela antes, pra minuta sair com os dados atuais */
      if (antesDeGerar) await antesDeGerar();
      const m = await gerarMinutaLead(leadId, selecionado); await carregar(); setAberta(m.id); } catch (e) { setErro(e.message); }
    setGerando(false);
  }
  async function excluir(mi) {
    if (!window.confirm(`Excluir a minuta "${mi.titulo}"?`)) return;
    try { await excluirMinuta(mi.id); carregar(); } catch (e) { setErro(e.message); }
  }

  return (
    <div className={styles.minutasLead}>
      <p className={styles.leadSecaoSub} style={{ margin: 0 }}>
        A minuta é preenchida com os dados deste lead (o que está na tela é salvo antes de gerar).
      </p>
      <div className={styles.configNovo}>
        <select className={styles.select} value={selecionado} onChange={e => setModeloSel(e.target.value)}>
          {ordenados.map(md => (
            <option key={md.id} value={md.id}>
              {sugestao.includes(md.tipo) ? '★ ' : ''}{md.nome} ({TIPOS_MODELO.find(t => t.id === md.tipo)?.label || md.tipo}){md.status === 'rascunho_revisar' ? ' – rascunho, revisar' : ''}
            </option>
          ))}
        </select>
        <button type="button" className={styles.btnAdicionarObrigacao} onClick={gerar} disabled={gerando || !selecionado}>
          <FiPlus size={15} /> {gerando ? 'Gerando...' : 'Gerar minuta'}
        </button>
      </div>
      {erro && <p className={styles.avisoReceitaErro}>{erro}</p>}
      {minutas.map(mi => (
        <div key={mi.id} className={styles.minutaLinha}>
          <button type="button" className={styles.checkLinkArquivo} onClick={() => setAberta(mi.id)}>
            <FiFileText size={14} /> {mi.titulo}
          </button>
          <span className={`${styles.minutaStatus} ${styles[`minutaStatus_${mi.status}`] || ''}`}>{nomeStatus(mi.status)}</span>
          <span className={styles.minutaInfo}>v{mi.versao_atual} · {dataHora(mi.atualizado_em)}</span>
          {mi.status === 'rascunho' && (
            <button type="button" className={styles.checkTirar} title="Excluir minuta" onClick={() => excluir(mi)}><FiX size={13} /></button>
          )}
        </div>
      ))}
      {!minutas.length && <p className={styles.leadSecaoSub}>Nenhuma minuta gerada ainda.</p>}
      {aberta && <EditorMinuta minutaId={aberta} onFechar={() => setAberta(null)} onMudou={carregar} />}
    </div>
  );
}

export default MinutasLead;
